import { useCallback, useMemo, useState } from 'react';
import { useNavigation } from '@react-navigation/native';
import { fetchUserBranches } from '../../auth/application/authBranches';
import {
  deactivateBranch,
  fetchInactiveBranchesForOwner,
  reactivateBranch,
  updateBranchIdentity,
  type BranchSummary,
} from '../../auth/infra/branches/branchesRepo';
import { patchSessionBranch } from '../../auth/infra/branches/branchSettingsSessionSync';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { isBillingEnabled } from '../../shell/billing/isBillingEnabled';
import {
  resolveBranchUsageForManage,
  resolveCanAddBranch,
} from './manageBranchesScreen.utils';
import {
  dismissSelfThenNavigateInShell,
  navigateRoot,
} from '../../shell/navigation/rootNavigation';
import { setLastSelectedBranchId } from '../../shared/localPrefs';
import { logBusinessAuditEvent } from '../../shell/audit/auditLogs';
import { showError, showSuccess } from '../../shared/ui/appToast';
import { presentUnexpectedFailure } from '../../shared/sentry/presentUnexpectedFailure';

const BRANCH_DEACTIVATE_CONFIRM_DELAY_SECONDS = 5;

export function useManageBranchesScreen() {
  const navigation = useNavigation<any>();
  const branches = useSessionStore((state) => state.branches);
  const currentBranchId = useSessionStore((state) => state.currentBranchId);
  const setCurrentBranch = useSessionStore((state) => state.setCurrentBranch);
  const setBranches = useSessionStore((state) => state.setBranches);
  const { entitlements, refresh: refreshEntitlements } = usePlanEntitlements();

  const [inactiveBranches, setInactiveBranches] = useState<BranchSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [reactivateTarget, setReactivateTarget] = useState<BranchSummary | null>(null);
  const [reactivating, setReactivating] = useState(false);
  const [deactivateTarget, setDeactivateTarget] = useState<BranchSummary | null>(null);
  const [deactivating, setDeactivating] = useState(false);
  const [editTarget, setEditTarget] = useState<BranchSummary | null>(null);
  const [editNameInput, setEditNameInput] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [crecePaywallVisible, setCrecePaywallVisible] = useState(false);

  const isOwner = entitlements?.is_owner === true;
  const activeBranchCount = branches.length;
  const billingOn = isBillingEnabled();

  /** Modo gratis: sin cupo de plan. Con billing: respetar entitlements. */
  const branchUsage = useMemo(() => {
    if (!billingOn) return null;
    return entitlements ? resolveBranchUsageForManage(entitlements, activeBranchCount) : null;
  }, [billingOn, entitlements, activeBranchCount]);

  const canAddBranch = useMemo(() => {
    if (!isOwner) return false;
    if (!billingOn) return true;
    return resolveCanAddBranch({
      isOwner,
      multiBranch: entitlements?.multi_branch,
      maxBranches: entitlements?.max_branches,
      activeBranchCount,
    });
  }, [isOwner, billingOn, entitlements?.multi_branch, entitlements?.max_branches, activeBranchCount]);

  const fleetLead = useMemo(() => {
    const n = activeBranchCount;
    if (n === 0) return 'Todavía no hay sucursales activas.';
    if (n === 1) return '1 sucursal activa.';
    return `${n} sucursales activas.`;
  }, [activeBranchCount]);

  const load = useCallback(async (options?: { force?: boolean }) => {
    const force = options?.force ?? true;
    if (!isOwner) {
      setInactiveBranches([]);
      setLoading(false);
      return;
    }
    try {
      const [active, inactive] = await Promise.all([
        fetchUserBranches({ force }),
        fetchInactiveBranchesForOwner(),
        refreshEntitlements({ force }),
      ]);
      setBranches(active);
      setInactiveBranches(inactive);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'No se pudieron cargar las sucursales.';
      showError('Error', msg);
    } finally {
      setLoading(false);
    }
  }, [isOwner, setBranches, refreshEntitlements]);

  const openBilling = useCallback(() => {
    setCrecePaywallVisible(true);
  }, []);

  const openActivateCrece = useCallback(() => {
    setCrecePaywallVisible(false);
    dismissSelfThenNavigateInShell(navigation, 'ActivateCrece');
  }, [navigation]);

  const dismissCrecePaywall = useCallback(() => {
    setCrecePaywallVisible(false);
  }, []);

  const openCreateBranch = useCallback(() => {
    if (!canAddBranch) {
      if (billingOn) {
        setCrecePaywallVisible(true);
        return;
      }
      showError(
        'No se puede agregar',
        'Solo el dueño puede agregar sucursales.',
      );
      return;
    }
    navigateRoot(navigation, 'CreateBranch');
  }, [canAddBranch, billingOn, navigation]);

  /** Activa: cambia sucursal (sin reset a Inicio) y abre pantalla Sucursal. */
  const openBranchIdentity = useCallback(
    (branchId: string) => {
      if (currentBranchId !== branchId) {
        // resetShell:false — el reset a MainTabs pisaba BranchIdentity en el 1er edit.
        setCurrentBranch(branchId, { resetShell: false });
        void setLastSelectedBranchId(branchId);
      }
      dismissSelfThenNavigateInShell(navigation, 'BranchIdentity');
    },
    [navigation, currentBranchId, setCurrentBranch],
  );

  /** Inactiva: solo nombre (no hay hub Sucursal sin reactivar). */
  const openBranchEdit = useCallback((branch: BranchSummary) => {
    setEditTarget(branch);
    setEditNameInput(branch.name);
  }, []);

  const closeBranchEdit = useCallback(() => {
    if (editSaving) return;
    setEditTarget(null);
    setEditNameInput('');
  }, [editSaving]);

  const confirmSaveBranchEdit = useCallback(async () => {
    if (!editTarget || editSaving) return;
    const nextName = editNameInput.trim();
    if (!nextName) {
      showError('Nombre requerido', 'Ingresá un nombre para la sucursal.');
      return;
    }

    setEditSaving(true);
    try {
      await updateBranchIdentity(editTarget.id, {
        name: nextName,
      });
      const isActive = branches.some((branch) => branch.id === editTarget.id);
      if (isActive) {
        patchSessionBranch(editTarget.id, { name: nextName });
      } else {
        setInactiveBranches((prev) =>
          prev.map((branch) =>
            branch.id === editTarget.id ? { ...branch, name: nextName } : branch,
          ),
        );
      }
      void logBusinessAuditEvent('branch.settings.identity', { branchId: editTarget.id });
      setEditTarget(null);
      showSuccess('Guardado', 'Sucursal actualizada.');
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'branch.update_identity', error: e, userMessage: 'No se pudo actualizar la sucursal.' });
    } finally {
      setEditSaving(false);
    }
  }, [editTarget, editSaving, editNameInput, branches]);

  const confirmReactivate = useCallback(async () => {
    if (!reactivateTarget || reactivating) return;
    setReactivating(true);
    try {
      await reactivateBranch(reactivateTarget.id);
      await fetchUserBranches({ force: true }).then(setBranches);
      await refreshEntitlements({ force: true });
      setInactiveBranches((prev) => prev.filter((b) => b.id !== reactivateTarget.id));
      setReactivateTarget(null);
      showSuccess('Sucursal reactivada', `Ya podés operar en ${reactivateTarget.name}.`);
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'branch.reactivate', error: e, userMessage: e instanceof Error ? e.message : 'No se pudo reactivar la sucursal.' });
    } finally {
      setReactivating(false);
    }
  }, [reactivateTarget, reactivating, setBranches, refreshEntitlements]);

  const confirmDeactivate = useCallback(async () => {
    if (!deactivateTarget || deactivating) return;
    setDeactivating(true);
    try {
      await deactivateBranch(deactivateTarget.id);
      const nextBranches = await fetchUserBranches({ force: true });
      setBranches(nextBranches);
      const inactive = await fetchInactiveBranchesForOwner();
      setInactiveBranches(inactive);
      await refreshEntitlements({ force: true });
      void logBusinessAuditEvent('branch.deactivated', { branchId: deactivateTarget.id });
      if (currentBranchId === deactivateTarget.id) {
        const nextActiveId = nextBranches[0]?.id ?? null;
        setCurrentBranch(nextActiveId);
        if (nextActiveId) {
          await setLastSelectedBranchId(nextActiveId);
        }
      }
      setDeactivateTarget(null);
      showSuccess('Sucursal desactivada', `${deactivateTarget.name} ya no aparece en el selector.`);
    } catch (e: unknown) {
      presentUnexpectedFailure({ module: 'settings', op: 'branch.deactivate', error: e, userMessage: e instanceof Error ? e.message : 'No se pudo desactivar la sucursal.' });
    } finally {
      setDeactivating(false);
    }
  }, [
    deactivateTarget,
    deactivating,
    setBranches,
    refreshEntitlements,
    currentBranchId,
    setCurrentBranch,
  ]);

  return {
    isOwner,
    activeBranches: branches,
    inactiveBranches,
    currentBranchId,
    loading,
    load,
    refreshEntitlements,
    branchUsage,
    fleetLead,
    canAddBranch,
    billingOn,
    reactivateTarget,
    reactivating,
    deactivateTarget,
    deactivating,
    branchDeactivateConfirmDelaySeconds: BRANCH_DEACTIVATE_CONFIRM_DELAY_SECONDS,
    setReactivateTarget,
    setDeactivateTarget,
    editTarget,
    editNameInput,
    editSaving,
    setEditNameInput,
    openCreateBranch,
    openBilling,
    openActivateCrece,
    dismissCrecePaywall,
    crecePaywallVisible,
    openBranchIdentity,
    openBranchEdit,
    closeBranchEdit,
    confirmSaveBranchEdit,
    confirmReactivate,
    confirmDeactivate,
  };
}

export type ManageBranchesScreenViewModel = ReturnType<typeof useManageBranchesScreen>;
